import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { IServiceRequest } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import { CsvDateFormat } from 'src/services/client-types';
import {
  useDownloadCSVData,
  UseDownloadCSVDataReturn,
} from 'src/pages/SharedPageCustomHooks/UseDownloadCSV/useDownloadCSVData';
import {
  useDownloadPDF,
  UseDownloadPDFReturn,
} from 'src/pages/SharedPageCustomHooks/UseDownloadPDF/useDownloadPDF';
import {
  useServiceRequestDashboardApi,
  UseServiceRequestDashboardApiReturn,
} from 'src/services/api/ServiceRequestDashboard/serviceRequestDashboard';
import {
  useScopedServiceRequestsFormState,
  UseScopedServiceRequestsFormStateReturn,
} from 'src/pages/ServiceRequests/CustomHooks/useScopedServiceRequestsFormState';

export interface UseItemServiceRequestsOrchestratorArgs {
  itemId: string;
  navigateTo: string;
  navLabel: string;
}

export interface UseItemServiceRequestsOrchestratorReturn {
  serviceRequests: IServiceRequest[];
  visibleServiceRequests: IServiceRequest[];
  isLoading: boolean;
  refetch: () => Promise<void>;

  searchQuery: string;
  setSearchQuery: (query: string) => void;

  showResolvedOnly: boolean;
  toggleShowResolved: () => void;

  selectedIds: string[];
  allVisibleSelected: boolean;
  idsForCsvExport: string[];
  toggleSelection: (id: string) => void;
  toggleSelectionBatch: (ids: string[]) => void;
  toggleSelectAllVisible: () => void;
  clearSelection: () => void;

  expandedGroupIds: string[];
  toggleGroupExpansion: (id: string) => void;

  isDownloadOverlayOpen: boolean;
  openDownloadOverlay: () => void;
  closeDownloadOverlay: () => void;

  onDownloadCSV: (dateFormat: CsvDateFormat) => Promise<boolean>;
  onDownloadPDF: () => Promise<boolean>;
  isCSVLoading: boolean;
  isPDFLoading: boolean;

  navigateTo: string;
  navLabel: string;

  showColumnSelectionDialog: boolean;
  openColumnSelectionDialog: () => void;
  closeColumnSelectionDialog: () => void;

  reset: () => void;
}

export const useItemServiceRequestsOrchestrator = ({
  itemId,
  navigateTo,
  navLabel,
}: UseItemServiceRequestsOrchestratorArgs): UseItemServiceRequestsOrchestratorReturn => {
  const user: any = useSelector(selectAuthenticatedUser);
  const userId: string = user?.id ?? '';

  const srDashboardApi: UseServiceRequestDashboardApiReturn =
    useServiceRequestDashboardApi();

  const serviceRequests: IServiceRequest[] = srDashboardApi.serviceRequests;
  const isLoading: boolean = srDashboardApi.listLoading;

  const formState: UseScopedServiceRequestsFormStateReturn =
    useScopedServiceRequestsFormState({ serviceRequests });

  const csvDownload: UseDownloadCSVDataReturn = useDownloadCSVData();
  const pdfDownload: UseDownloadPDFReturn = useDownloadPDF();

  const onDownloadCSV = async (dateFormat: CsvDateFormat): Promise<boolean> => {
    return await csvDownload.onDownloadCSV(formState.idsForCsvExport, dateFormat);
  };

  const onDownloadPDF = async (): Promise<boolean> => {
    return await pdfDownload.onDownloadPDF(
      userId,
      formState.idsForCsvExport.join('&')
    );
  };

  const refetch = async (): Promise<void> => {
    formState.clearSelection();
    await srDashboardApi.fetchByItem(itemId);
  };

  useEffect(() => {
    if (itemId) {
      void refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  const reset = (): void => {
    formState.reset();
  };

  return {
    serviceRequests,
    visibleServiceRequests: formState.visibleServiceRequests,
    isLoading,
    refetch,

    searchQuery: formState.searchQuery,
    setSearchQuery: formState.setSearchQuery,

    showResolvedOnly: formState.showResolvedOnly,
    toggleShowResolved: formState.toggleShowResolved,

    selectedIds: formState.selectedIds,
    allVisibleSelected: formState.allVisibleSelected,
    idsForCsvExport: formState.idsForCsvExport,
    toggleSelection: formState.toggleSelection,
    toggleSelectionBatch: formState.toggleSelectionBatch,
    toggleSelectAllVisible: formState.toggleSelectAllVisible,
    clearSelection: formState.clearSelection,

    expandedGroupIds: formState.expandedGroupIds,
    toggleGroupExpansion: formState.toggleGroupExpansion,

    isDownloadOverlayOpen: formState.isDownloadOverlayOpen,
    openDownloadOverlay: formState.openDownloadOverlay,
    closeDownloadOverlay: formState.closeDownloadOverlay,

    onDownloadCSV,
    onDownloadPDF,
    isCSVLoading: csvDownload.isLoading,
    isPDFLoading: pdfDownload.isLoading,

    navigateTo,
    navLabel,

    showColumnSelectionDialog: formState.showColumnSelectionDialog,
    openColumnSelectionDialog: formState.openColumnSelectionDialog,
    closeColumnSelectionDialog: formState.closeColumnSelectionDialog,

    reset,
  };
};